import { apiClient } from './api';

type Pagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type BulkWinesListResponse = {
  data: any[];
  pagination: Pagination;
};

export type BlobDownloadResult = {
  blob: Blob;
  filename: string | null;
};

export type EmailSingleRequest = {
  id: number;
  replyTo: string;
  recipients: string[];
  cc?: string[];
  subject: string;
  body: string;
  bodyPlainText: string;
};

export type EmailMultipleRequest = {
  ids: number[];
  replyTo: string;
  recipients: string[];
  cc?: string[];
  subject: string;
  body: string;
  bodyPlainText: string;
};

export const bulkWinesService = {
  getList: (params: URLSearchParams) =>
    apiClient.get<BulkWinesListResponse>(`/bulk-wines?${params.toString()}`),

  getById: (id: string | number) => apiClient.get<any>(`/bulk-wines/${id}`),

  generateCode: (vintage: number, varietyId?: number | null) => {
    const params = new URLSearchParams({ vintage: String(vintage) });
    if (varietyId) {
      params.set('varietyId', String(varietyId));
    }
    return apiClient.get<{ code: string }>(`/bulk-wines/generate-code?${params.toString()}`);
  },

  save: (id: string | number | null, payload: Record<string, unknown>) =>
    id
      ? apiClient.put<any, Record<string, unknown>>(`/bulk-wines/${id}`, payload)
      : apiClient.post<any, Record<string, unknown>>('/bulk-wines', payload),

  remove: (id: string | number) =>
    apiClient.delete<{ id: number; message: string }>(`/bulk-wines/${id}`),

  downloadPdf: async (id: string | number): Promise<BlobDownloadResult> => {
    const { data, headers } = await apiClient.getWithHeaders<Blob>(
      `/bulk-wines/${id}/pdf`,
      { responseType: 'blob' },
    );
    return {
      blob: data,
      filename: headers['content-disposition'] ?? null,
    };
  },

  downloadPdfZip: async (ids: number[]): Promise<BlobDownloadResult> => {
    const { data, headers } = await apiClient.postWithHeaders<Blob, { ids: number[] }>(
      '/bulk-wines/pdf-zip',
      { ids },
      { responseType: 'blob', timeout: 120000 },
    );
    return {
      blob: data,
      filename: headers['content-disposition'] ?? null,
    };
  },

  emailSingle: async (request: EmailSingleRequest): Promise<void> => {
    await apiClient.post<void, EmailSingleRequest>('/bulk-wines/email-single', request);
  },

  emailMultiple: async (request: EmailMultipleRequest): Promise<void> => {
    await apiClient.post<void, EmailMultipleRequest>('/bulk-wines/email-multiple', request, {
      timeout: 120000,
    });
  },
};
